import { PDFDocument, PDFFont, PDFPage, rgb } from "pdf-lib";

export const wrapTextLines = (
  text: string,
  font: PDFFont,
  fontSize: number,
  maxWidth: number
): string[] => {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(test, fontSize) > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });

  if (current) lines.push(current);
  return lines.length ? lines : [""];
};

export interface DrawTextBlockOptions {
  page: PDFPage;
  font: PDFFont;
  boldFont?: PDFFont;
  x: number;
  y: number;
  maxWidth: number;
  fontSize?: number;
  lineHeight?: number;
  color?: ReturnType<typeof rgb>;
  bottomMargin?: number;
}

export const drawTextBlock = (
  text: string,
  options: DrawTextBlockOptions
): number => {
  const {
    page,
    font,
    x,
    y,
    maxWidth,
    fontSize = 11,
    lineHeight = 14,
    color = rgb(0.1, 0.1, 0.15),
    bottomMargin = 60,
  } = options;

  let cursorY = y;
  const lines = wrapTextLines(text, font, fontSize, maxWidth);

  lines.forEach((line) => {
    if (cursorY < bottomMargin) {
      return;
    }
    page.drawText(line, { x, y: cursorY, size: fontSize, font, color });
    cursorY -= lineHeight;
  });

  return cursorY;
};

export const drawHeading = (
  page: PDFPage,
  font: PDFFont,
  text: string,
  x: number,
  y: number,
  size = 14
): number => {
  page.drawText(text, {
    x,
    y,
    size,
    font,
    color: rgb(0.05, 0.15, 0.35),
  });
  return y - size - 8;
};

export const createPdfDocument = async () => {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont("Helvetica");
  const boldFont = await pdfDoc.embedFont("Helvetica-Bold");
  return { pdfDoc, font, boldFont };
};

export const downloadPdfBytes = (bytes: Uint8Array, fileName: string) => {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
